import { useApolloClient } from "@apollo/client";
import { Button } from "@chakra-ui/react";
import React, { useCallback } from "react";
import { RiArchiveLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { archiveTeamMembers } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import { GET_MEMBERS } from "./graphql/queries";

type Props = {
  person: {
    person_id: string;
    active: boolean;
    full_name: string;
    email_address: string;
  };
};

export const ArchiveTeamMemberButton: React.FC<Props> = ({ person }) => {
  const organization = useOrganization();
  const navigate = useNavigate();

  const name = isNonEmptyString(person.full_name)
    ? person.full_name
    : person.email_address;
  const archiveDialog = useConfirmationDialog(
    person.active
      ? {
          header: `Archive ${name}`,
          body: "Archiving this team member will remove all access to the organization for them.",
          confirmButton: {
            label: "Yes, archive",
          },
        }
      : {
          header: `Unarchive ${name}`,
          body: "Unarchiving this team member will grant all access to the organization for them.",
          confirmButton: {
            label: "Yes, unarchive",
          },
        }
  );
  const apolloClient = useApolloClient();
  const toast = useAvelaToast();
  const archiveTeamMemberHandler = useCallback(async () => {
    if (!organization.hasData()) {
      throw new Error("Invalid organization");
    }
    if (
      await archiveDialog.confirm({
        onConfirm: (helpers) => {
          helpers.setButton({
            isLoading: true,
            loadingText: person.active ? "Archiving..." : "Unarchiving...",
            label: "",
          });
        },
      })
    ) {
      try {
        await archiveTeamMembers({
          body: [{ personId: person.person_id, active: !person.active }],
        });
        await apolloClient.refetchQueries({
          include: [GET_MEMBERS],
          updateCache: (cache) => {
            cache.evict({ fieldName: "person" });
            cache.gc();
          },
        });
        archiveDialog.helpers.close();
        navigate(Url.OrgAdmin.Team.index(organization.data));
      } catch (error) {
        console.error(error);
        archiveDialog.helpers.close();
        toast.genericError();
      }
    }
  }, [
    organization,
    archiveDialog,
    person.active,
    person.person_id,
    apolloClient,
    navigate,
    toast,
  ]);

  return (
    <>
      <Button
        variant="outline"
        leftIcon={<RiArchiveLine />}
        alignSelf="flex-start"
        onClick={archiveTeamMemberHandler}
      >
        {person.active ? "Archive member" : "Unarchive member"}
      </Button>
      {archiveDialog.confirmationDialog}
    </>
  );
};
